import { useMutation, useQueryClient } from '@tanstack/react-query';

import { isNotDefined, isOfType } from '@hsp/utilities';

import { useAppSelector } from 'hooks/store';
import { dashboardQueryKeys } from 'pages/audience/dashboard/hooks';
import { useDashboardStore } from 'pages/audience/dashboard/store';
import { useSaveStoreSelector } from 'pages/audience/shared/save/hooks/context';
import { userService } from 'services';
import { selectUserId } from 'store/selectors';
import { IAudience } from 'types/api/audience';
import { IResult } from 'types/api/common';
import { SaveForm } from 'types/pages/audience-builder/shared/save';

export const useUpdateAudience = () => {
  const userId = useAppSelector(selectUserId);

  const data = useSaveStoreSelector(({ data }) => data);
  const tab = useDashboardStore(({ tab }) => tab);

  const queryClient = useQueryClient();

  const queryKey = dashboardQueryKeys.audiences.byTab(tab);

  return useMutation({
    mutationFn: async (formData: SaveForm) => {
      const { id, type } = formData;

      if (isNotDefined(id)) {
        return Promise.reject();
      }

      if (
        type === 'basic' ||
        type === 'master-file' ||
        type === 'pre-built' ||
        !isOfType<IAudience>(data, 'category')
      ) {
        return Promise.reject();
      }

      const { client, giftDateEnd, giftDateStart, ...rest } = formData;
      const audience: IAudience = {
        ...data,
        ...rest,
        clientId: client?.id,
        clientName: client?.name,
        giftDateEnd: giftDateEnd?.toISOString(),
        giftDateStart: giftDateStart?.toISOString(),
        id,
      };

      return await userService.updateAudience({ data: audience, id, userId });
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.snapshot);
    },
    onMutate: async (formData: SaveForm) => {
      const { id } = formData;

      await queryClient.cancelQueries({ queryKey });

      const snapshot = queryClient.getQueriesData<IResult<IAudience[]>>({ queryKey });

      queryClient.setQueriesData<IResult<IAudience[]>>({ queryKey }, (oldData) => {
        if (isNotDefined(oldData)) {
          return;
        }

        const { dataContainer } = oldData;
        const { giftDateEnd, giftDateStart } = formData;

        if (isNotDefined(dataContainer)) {
          return oldData;
        }

        const index = dataContainer.findIndex((item) => item.id === id);

        if (index === -1) {
          return oldData;
        }

        return {
          ...oldData,
          dataContainer: [
            ...dataContainer.slice(0, index),
            {
              ...dataContainer[index],
              ...{
                ...formData,
                giftDateEnd: giftDateEnd?.toISOString(),
                giftDateStart: giftDateStart?.toISOString(),
              },
            },
            ...dataContainer.slice(index + 1),
          ] as IAudience[],
        };
      });

      return {
        snapshot,
      };
    },
    onSettled: (_data, _error) => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};
